import { useEffect } from 'react';
import { useWindowSize } from 'react-use';
import styled from 'styled-components';
import router from 'next/router';
import { NextSeo } from 'next-seo';
import { useForm } from 'react-hook-form';

import { useTranslation } from '@hooks';
import { useNotification } from '@context/NotificationContext';
import { useAuth } from 'context/AuthContext';
import { InputWrapper } from 'components/common';
import { Caption2 } from 'components/styles/Typography';
import { NewButton } from '@components/ui';
import PageHeader from '@components/common/Header/PageHeader';
import { forgetPasswordSchema } from 'lib/validationSchemas';
import { AuthPageTitle } from '@components/auth';
import { ForgotPasswordWrapper } from '@components/styles/AuthStyles';

const { yupResolver } = require('@hookform/resolvers/yup');

const ForgetPasswordPage = () => {
    const { t } = useTranslation();
    const { forgetPasswordMutation, currentUser } = useAuth();
    const { open } = useNotification();
    const { width } = useWindowSize();
    const { mutate, error } = forgetPasswordMutation;

    const { register, handleSubmit } = useForm({
        resolver: yupResolver(forgetPasswordSchema),
    });

    useEffect(() => {
        if (currentUser) {
            router.push('/');
        }
    }, [currentUser]);

    const onSubmit = (data) =>
        mutate(
            { email: data.email },
            {
                onSuccess: (data) => {
                    if (data?.success) {
                        open({
                            type: 'success',
                            title: t('resetLinkSended'),
                        });
                    }
                },
            },
        );

    return (
        <>
            <NextSeo title={`${t('ferventeTitle')} ${t('forgotMyPassword')}`} />
            <ForgotPasswordWrapper>
                {width > 1024 ? (
                    <AuthPageTitle title={t('forgotPasswordUpper')} />
                ) : (
                    <PageHeader title={t('forgotPassword')} noBorderHeader />
                )}
                <ForgotPasswordForm onSubmit={handleSubmit(onSubmit)}>
                    <CaptionWrapper>
                        <Caption2>{t('forgotPasswordSendMail')}</Caption2>
                    </CaptionWrapper>
                    <InputWrapper
                        name="email"
                        placeholder="E-mail"
                        width="100%"
                        error={error}
                        errorMessage={t('userNotFound')}
                    >
                        <input {...register('email')} />
                    </InputWrapper>
                    <ButtonWrapper>
                        <NewButton
                            size="xlarge"
                            type="primary"
                            content={t('send')}
                            handleClick={handleSubmit(onSubmit)}
                        />
                    </ButtonWrapper>
                </ForgotPasswordForm>
            </ForgotPasswordWrapper>
        </>
    );
};

const ForgotPasswordForm = styled.form`
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 345px;

    @media (max-width: 1024px) {
        max-width: 100%;
        padding: 40px 0;
    }
`;
const CaptionWrapper = styled.div`
    margin-bottom: 40px;
`;
const ButtonWrapper = styled.div`
    width: 100%;
    margin-top: 55px;

    @media (max-width: 1024px) {
        margin-top: 40px;
    }
`;

export default ForgetPasswordPage;
